import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";
import { logAuditAction } from "@/lib/audit";

export const collaboratorSchema = z.object({
  name: z.string().min(2, "Nome obrigatório"),
  phone: z.string().nullable().optional(),
  specialty: z.string().nullable().optional(),
  system_role: z.enum(["ADMIN", "COLABORADOR", "ATENDENTE", "CAIXA"]),
  commission_rate: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? 10 : Number(val),
    z.number().min(0).max(100),
  ),
  status: z.preprocess(
    (val) => (val === null || val === undefined ? "ATIVO" : val),
    z.enum(["ATIVO", "INATIVO"]),
  ),
});
export type CollabFormValues = z.infer<typeof collaboratorSchema>;

export function formatPhone(value: string) {
  const v = value.replace(/\D/g, "");
  if (v.length === 0) return "";
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10)
    return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7, 11)}`;
}

export function useTeamData(
  activeCompanyId: string | null,
  activeTab: "equipe" | "auditoria",
) {
  const queryClient = useQueryClient();

  const { data: collaborators = [], isLoading: isLoadingTeam } = useQuery({
    queryKey: ["collaborators", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("collaborators")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("status", { ascending: true })
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  const { data: teamOrders = [], isLoading: isLoadingOrders } = useQuery({
    queryKey: ["team_orders", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select(
          "total, assigned_to, status, created_at, completed_at, services, stage_timestamps",
        )
        .eq("company_id", activeCompanyId)
        .in("status", ["CONCLUIDO", "PAGO"])
        .not("assigned_to", "is", null);
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  const { data: auditLogs = [], isLoading: isLoadingLogs } = useQuery({
    queryKey: ["audit_logs", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("audit_logs")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("timestamp", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId && activeTab === "auditoria",
  });

  const saveCollab = useCompanyMutation({
    mutationFn: async (
      variables: CollabFormValues & { company_id?: string; id?: string },
    ) => {
      if (variables.id) {
        const { data, error } = await supabase
          .from("collaborators")
          .update({
            name: variables.name,
            phone: variables.phone || null,
            specialty: variables.specialty || null,
            system_role: variables.system_role,
            commission_rate: variables.commission_rate,
            status: variables.status,
          })
          .eq("id", variables.id)
          .eq("company_id", activeCompanyId)
          .select();

        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error(
            "Falha ao atualizar. O colaborador não foi encontrado.",
          );
        }
      } else {
        const { error } = await supabase.from("collaborators").insert({
          id: crypto.randomUUID(),
          company_id: variables.company_id,
          name: variables.name,
          phone: variables.phone,
          specialty: variables.specialty,
          system_role: variables.system_role,
          commission_rate: variables.commission_rate,
          status: variables.status,
        });
        if (error) throw error;
      }
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["collaborators"] });
      await logAuditAction({
        companyId: activeCompanyId || "",
        type: "COLLABORATOR_UPDATE",
        details: `${variables.id ? "Editou" : "Criou"} o colaborador ${variables.name} (Comissão: ${variables.commission_rate}%)`,
      });
    },
    onError: (err: any) => {
      alert(`Erro ao salvar: ${err.message}`);
    },
  });

  const toggleStatus = useCompanyMutation({
    mutationFn: async ({
      id,
      newStatus,
    }: {
      id: string;
      newStatus: string;
    }) => {
      const { error } = await supabase
        .from("collaborators")
        .update({ status: newStatus })
        .eq("id", id)
        .eq("company_id", activeCompanyId);
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["collaborators"] }),
  });

  const payCommission = useCompanyMutation({
    mutationFn: async ({
      collabId,
      amount,
      collabName,
      method,
    }: {
      collabId: string;
      amount: number;
      collabName: string;
      method: string;
    }) => {
      if (!activeCompanyId) throw new Error("Empresa não identificada");

      const { error: txError } = await supabase
        .from("cash_transactions")
        .insert({
          id: crypto.randomUUID(),
          company_id: activeCompanyId,
          type: "SAIDA",
          amount: amount,
          method: method,
          description: `Pagamento de Comissão - ${collabName}`,
          category: "Pessoal",
        });
      if (txError) throw txError;

      const collab = collaborators.find((c: any) => c.id === collabId);
      const currentPaid = collab?.total_paid_commission || 0;

      const { error: collabError } = await supabase
        .from("collaborators")
        .update({ total_paid_commission: currentPaid + amount })
        .eq("id", collabId)
        .eq("company_id", activeCompanyId);
      if (collabError) throw collabError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborators"] });
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      queryClient.invalidateQueries({ queryKey: ["financial_transactions"] });
    },
    onError: (err: any) => {
      alert(`Erro ao pagar comissão: ${err.message}`);
    },
  });

  const generateInvite = useCompanyMutation({
    mutationFn: async ({
      role,
      company_id,
    }: {
      role: string;
      company_id?: string;
    }) => {
      if (!company_id) throw new Error("Empresa não identificada");
      const { data, error } = await supabase
        .from("tenant_invites")
        .insert({
          company_id,
          role,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        })
        .select("id")
        .single();

      if (error) throw error;
      return data;
    },
    onError: (err: any) => alert(`Erro ao gerar convite: ${err.message}`),
  });

  return {
    collaborators,
    teamOrders,
    auditLogs,
    isLoading: isLoadingTeam || isLoadingOrders,
    isLoadingLogs,
    saveCollab,
    toggleStatus,
    payCommission,
    generateInvite,
  };
}
